import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from "react-i18next";
import { BlogService } from '../Blog/services/BlogService';
import { IPartner } from '../../utils/ArticleInterface';
import { IArticleCategory } from '../../utils/ArticleInterface';
import { Theme } from '@material-ui/core';

const useStyles = makeStyles((theme: Theme) => ({
    navbar: {
        display: 'flex',
        justifyContent: 'flex-start',
        backgroundColor: 'rgb(221, 221, 221)',  // Light grey background
        height: 'auto',  // Allow navbar to adjust its height automatically
        padding: '15px 0px 15px 100px',  // Adjust padding to fit within the design
        borderBottom: '1px solid #ddd',
        width: '100%',
        boxSizing: 'border-box',
        zIndex: 999,
        position: 'relative',
        overflow: 'visible',
        whiteSpace: 'nowrap',
        [theme.breakpoints.down('sm')]: {
            padding: '0 10px', // Padding for mobile
        },
    },
    navItems: {
        display: 'flex',
        whiteSpace: 'nowrap',  // Prevent text from wrapping to the next line
        alignItems: 'center',  // Vertically center the text inside the navbar
        justifyContent: 'flex-start',  // Align text to the left
    },
    navLink: {
        fontSize: '14px',
        color: '#000',
        marginRight: '20px',
        textDecoration: 'none',
        textTransform: 'uppercase',
        fontWeight: 'bold',
        fontFamily: 'Roboto, sans-serif',
        border: 'none',  // Remove border
        outline: 'none',  // Remove outline on focus
        background: 'none',  // Ensure the button has no background
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    dropdown: {
        position: 'relative',  // Ensure the dropdown is positioned relative
        display: 'inline-block',
    },
    dropdownContent: {
        display: 'none',
        position: 'absolute',
        top: '100%',  // Align the dropdown just below the button
        left: '0',
        backgroundColor: '#fff',
        minWidth: '160px',
        zIndex: 1500,
        boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.2)',  // Optional box-shadow for dropdown
        borderRadius: '8px',  // Rounded corners
        overflow: 'visible',  
        '& a': {
            color: '#000',
            fontWeight: 'bold',
            fontSize: '14px',
            padding: '12px 16px',
            textDecoration: 'none',
            fontFamily: 'Roboto, sans-serif',
            display: 'block',
            borderBottom: '1px solid #ddd',  // Light grey separator between items
            '&:last-child': {
                borderBottom: 'none',  // Remove bottom border for the last item
            },
            '&:hover': {
                backgroundColor: '#ddd',
            },
        },
    },
    dropdownShow: {
        display: 'block',
    },
}));

export interface ICategory {
    _id: string;
    name: string;  // Since name is a string in the fetched data
    slug: string;
}

const CACHE_TTL = 3600000;

const NavbarBlog = () => {
    const classes = useStyles();
    const { t } = useTranslation<'common'>();
    const [showCategories, setShowCategories] = useState(false);
    const [showPartners, setShowPartners] = useState(false);
    const [categories, setCategories] = useState<ICategory[]>([]); // Explicitly define type for categories
    const [partners, setPartners] = useState<IPartner[]>([]); // Explicitly define type for partners
    const location = useLocation();
    const blogService = useMemo(() => new BlogService(), []);

    useEffect(() => {
        const CACHE_KEY_CATEGORIES = 'blogCategories';
        const CACHE_KEY_PARTNERS = 'blogPartners';

        // Fetch categories with cache check
        const fetchCategories = async () => {
            const cachedData = localStorage.getItem(CACHE_KEY_CATEGORIES);
            if (cachedData) {
                const { data, timestamp } = JSON.parse(cachedData);
                if (Date.now() - timestamp < CACHE_TTL) {
                    setCategories(data);
                    return; // Use cached data
                }
            }

            try {
                const response = await blogService.getAllCategoriesNav();
                const categoriesList = response
                    .filter((category: IArticleCategory) => category.categoryDropdown === 'true')
                    .map((category: IArticleCategory) => ({
                        _id: category._id,
                        name: category.name.fr,
                        slug: category.name.slug,
                    }));
                setCategories(categoriesList);
                localStorage.setItem(CACHE_KEY_CATEGORIES, JSON.stringify({
                    data: categoriesList,
                    timestamp: Date.now()
                }));
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };

        // Fetch partners with cache check
        const fetchPartners = async () => {
            const cachedData = localStorage.getItem(CACHE_KEY_PARTNERS);
            if (cachedData) {
                const { data, timestamp } = JSON.parse(cachedData);
                if (Date.now() - timestamp < CACHE_TTL) {
                    setPartners(data);
                    return; // Use cached data
                }
            }

            try {
                const response = await blogService.getAllPartnersNav();
                const partnersList = response
                    .filter((partner: IPartner) => partner.partnerDropdown === "true")
                    .map((partner: IPartner) => ({
                        _id: partner._id,
                        name: partner.name,
                        slug: partner.slug || slugify(partner.name),
                    }));
                setPartners(partnersList);
                localStorage.setItem(CACHE_KEY_PARTNERS, JSON.stringify({
                    data: partnersList,
                    timestamp: Date.now()
                }));
            } catch (error) {
                console.error('Error fetching partners:', error);
            }
        };

        fetchCategories();
        fetchPartners();
    }, [blogService]);

    const slugify = (str: string | undefined): string => {
        if (!str) return '';
        
        return str
            .toString()
            // First convert common accented characters explicitly
            .replace(/[éèêë]/gi, 'e')
            .replace(/[àâä]/gi, 'a')
            .replace(/[îï]/gi, 'i')
            .replace(/[ôö]/gi, 'o')
            .replace(/[ùûü]/gi, 'u')
            .replace(/ç/gi, 'c')
            // Then handle remaining diacritics systematically
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim()
            .replace(/['"]/g, '-')
            .replace(/\s+/g, '-')
            .replace(/[^\w\-]+/g, '')
            .replace(/--+/g, '-') 
            .replace(/^-+/, '')
            .replace(/-+$/, '');
    };

    // Only render on /blog pages
    if (!location.pathname.startsWith('/blog')) {
        return null;
    }

    return (
        <nav className={classes.navbar}>
            <div className={classes.navItems}>
                <Link to="/blog" className={classes.navLink}>
                    {t("blogNavbar.accueil")}
                </Link>

                {/* Categories Dropdown */}
                <div
                    className={classes.dropdown}
                    onMouseEnter={() => setShowCategories(true)}  // Show on hover
                    onMouseLeave={() => setShowCategories(false)}  // Hide when not hovered
                >
                    <button className={classes.navLink}>
                        {t("blogNavbar.nos-rubriques")}
                    </button>
                    <div className={`${classes.dropdownContent} ${showCategories ? classes.dropdownShow : ''}`}>
                        {categories.map((data) => (
                            <Link to={`/blog/rubrique/${data.slug}`} key={data._id}>
                                {data.name}
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Partners Dropdown */}
                <div
                    className={classes.dropdown}
                    onMouseEnter={() => setShowPartners(true)}  // Show on hover
                    onMouseLeave={() => setShowPartners(false)}  // Hide when not hovered
                >
                    <button className={classes.navLink}>
                        {t("blogNavbar.partenaires")}
                    </button>
                    <div className={`${classes.dropdownContent} ${showPartners ? classes.dropdownShow : ''}`}>
                        {partners.map(partner => (
                            <Link to={`/blog/partenaires/${partner.slug}`} key={partner._id}>
                                {partner.name}
                            </Link>
                        ))}
                    </div>
                </div>
                {/*
                <Link to="/blog/guides" className={classes.navLink}>
                    {t("blogNavbar.guides")}
                </Link>
                <Link to="/blog/partenaires/can-maroc-2025" className={classes.navLink}>
                    {t("blogNavbar.maroc2025")}
                </Link>
                */}
            </div>
        </nav>
    );
};

export default NavbarBlog;
